import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Card, Center, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { LanguageButton } from '../components/LanguageButton';
import { PlatformLogo } from '../components/platform-logo';
import { tr } from '../lib/i18n';

const Schema = z.object({
  login: z.string().trim().min(3, tr('أدخل اسم المستخدم أو البريد', 'Enter your username or email')),
  password: z.string().min(1, tr('أدخل كلمة المرور', 'Enter your password')),
});
type Form = z.infer<typeof Schema>;

export function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [error, setError] = useState<string>();
  const { register, handleSubmit, formState } = useForm<Form>({ resolver: zodResolver(Schema) });

  const submit = handleSubmit(async (v) => {
    setError(undefined);
    try {
      await login(v.login, v.password);
      nav((loc.state as { from?: string } | null)?.from ?? '/', { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : tr('تعذر الاتصال بالخادم', 'Could not connect to the server'));
    }
  });

  return (
    <Box mih="100dvh" style={{ overflowY: 'auto', backgroundImage: 'radial-gradient(var(--pf-line) 1px, transparent 1px)', backgroundSize: '22px 22px' }}>
      <Box pos="fixed" top={12} style={{ insetInlineEnd: 12 }}>
        <LanguageButton labeled />
      </Box>
      <Center mih="100dvh" p="md">
        <Card w="100%" maw={400} padding="xl" shadow="md">
          <Stack gap={4} align="center" mb="xl">
            <PlatformLogo size={44} />
            <Title order={1} fz={20} mt="sm">{tr('لوحة المنصة', 'Platform console')}</Title>
            <Text size="sm" c="dimmed">{tr('دخول مدير FormTags', 'FormTags admin sign-in')}</Text>
          </Stack>
          <form onSubmit={submit} noValidate>
            <Stack gap="md">
              {error && <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />}>{error}</Alert>}
              <TextInput label={tr('اسم المستخدم أو البريد', 'Username or email')} autoComplete="username" autoFocus dir="ltr" size="md" {...register('login')} error={formState.errors.login?.message} />
              <PasswordInput label={tr('كلمة المرور', 'Password')} autoComplete="current-password" dir="ltr" size="md" {...register('password')} error={formState.errors.password?.message} />
              <Button type="submit" size="md" fullWidth loading={formState.isSubmitting} mt="xs">{tr('دخول', 'Sign in')}</Button>
            </Stack>
          </form>
        </Card>
      </Center>
    </Box>
  );
}
